import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PlatformReview {
  id: string;
  user_id?: string;
  rating: number;
  title: string | null;
  comment: string;
  category: string;
  verified_purchase: boolean;
  status: string;
  helpful_count: number;
  unhelpful_count: number;
  user_display_name: string | null;
  user_avatar_url: string | null;
  user_country: string | null;
  created_at: string;
  approved_at: string | null;
}

export interface ReviewsAggregate {
  total_reviews: number;
  avg_rating: number;
  count_5: number;
  count_4: number;
  count_3: number;
  count_2: number;
  count_1: number;
  verified_count: number;
}

export function useApprovedReviews(limit = 12, category?: string) {
  const [reviews, setReviews] = useState<PlatformReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("platform_reviews_public" as any)
      .select("id, rating, title, comment, category, verified_purchase, status, helpful_count, unhelpful_count, user_display_name, user_avatar_url, user_country, created_at, approved_at")
      .order("approved_at", { ascending: false })
      .limit(limit);
    if (category) query = query.eq("category", category as any);
    const { data, error } = await query;
    if (error) setError(error.message);
    else setReviews(((data ?? []) as unknown) as PlatformReview[]);
    setLoading(false);
  }, [limit, category]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  return { reviews, loading, error, refresh: fetchReviews };
}

export function useReviewsAggregate() {
  const [agg, setAgg] = useState<ReviewsAggregate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase
      .from("reviews_aggregate" as any)
      .select("*")
      .maybeSingle()
      .then(({ data }) => {
        if (!mounted) return;
        setAgg((data as unknown as ReviewsAggregate) ?? null);
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  return { aggregate: agg, loading };
}

export function useSubmitReview() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (payload: {
    rating: number;
    title?: string;
    comment: string;
    category: string;
  }) => {
    setSubmitting(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke("submit-review", { body: payload });
      if (error) throw error;
      return data;
    } catch (e: any) {
      const msg = e?.message ?? "Failed to submit review";
      setError(msg);
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  return { submit, submitting, error };
}
